import React from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useForm, Controller } from 'react-hook-form'
import { useMutation } from '@apollo/react-hooks'
import qs from 'query-string'

import { getConfigValue } from '../../../utils/getConfigValue'
import { modalContent } from '../../../modules/shared/subscription-constants'
import Input from '../../form-fields/input'
import Select from '../../form-fields/select'
import Button from '../../button'
import FullScreenLoadingComponent from '../../loading/fullscreen-loader'

import {
  STATIC_SCREENS,
  FORM_MESSAGES,
  FORM_VALUES
} from '../../../constants'
import { useConfig } from '../../../hooks/use-config'
import { getGQLErrorMsg } from '../../../lib/apollo'
import getGenderOptions from '../../../utils/getGenderOptions'
import { updateAuthToken } from '../../../actions'
import ACCOUNT_MUTATION from '../../../../graphql/mutations/account.gql'
import styles from './index.css'

/**
 * Displays a modal prompting users to complete their account details
 */
const CompleteAccountDetails = ({
  renderStep,
  isRental,
  onCompleteAccountDetails
}) => {
  const title = `Finish your account set up - NEON`
  const header = `Finish your account set up`

  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()

  const [updateAccount, { loading: isUpdatingAccount }] = useMutation(ACCOUNT_MUTATION)

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    trigger
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      birthYear: '',
      gender: ''
    }
  })

  // Birth year validation constants
  const { appConfig } = useConfig()
  const currentYear = new Date().getFullYear()
  const ACCOUNT_MIN_AGE = getConfigValue(appConfig.values, 'ACCOUNT_SIGNUP_MIN_AGE')
  const ACCOUNT_MAX_AGE = getConfigValue(appConfig.values, 'ACCOUNT_SIGNUP_MAX_AGE')
  const minBirthYear = currentYear - ACCOUNT_MAX_AGE
  const maxBirthYear = currentYear - ACCOUNT_MIN_AGE

  // Gender options
  const genderOptions = getGenderOptions()

  const onSubmit = async (formData) => {
    try {
      const { data } = await updateAccount({
        variables: {
          input: {
            name: formData.firstName,
            surname: formData.lastName,
            birthYear: formData.birthYear,
            gender: formData.gender
          }
        }
      })

      // Destructure account from response
      const account = data?.account

      // Handle GraphQL errors
      const firstError = account?.errors?.[0]?.message
      if (firstError) {
        setError('root.formError', { message: firstError })
        return
      }

      // If we have a token, update the auth token in Redux
      // This allows us to send the updated user information (e.g. first name and last name) to the data layer
      const token = account?.session?.token
      if (token) {
        dispatch(updateAuthToken(token, true))
      }

      // Navigate to the next step in the login-rental modal flow (next step: 2)
      if (renderStep && isRental) {
        renderStep(2)
        return
      }

      // Add voucher flow only: hand control back to parent to continue to payment view.
      if (onCompleteAccountDetails) {
        onCompleteAccountDetails()
        return
      }

      // Navigate to the payment page during the add subscription modal flow
      history.push({
        ...location,
        search: qs.stringify({
          ...qs.parse(location.search),
          content: modalContent.payment
        })
      })
    } catch (error) {
      // Handle network or GraphQL errors
      const errorMessage = error.graphQLErrors
        ? getGQLErrorMsg(error)
        : error.message || 'An error occurred. Please try again.'

      setError('root.formError', { message: errorMessage })
    }
  }

  return (
    <div className={styles.wrapper}>
      <Helmet><title>{title}</title></Helmet>
      <div>
        <h1 className={styles.header}>{header}</h1>
        <div className={styles.container}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            autoComplete="off"
          >
            <Controller
              name="firstName"
              control={control}
              rules={{
                required: FORM_MESSAGES.required
              }}
              render={({ field }) => (
                <Input
                  label="First name"
                  {...field}
                  error={errors[field.name]?.message}
                  onBlur={() => {
                    field.onBlur()
                  }}
                  disabled={isUpdatingAccount}
                  data-e2e="first-name-account-details"
                  autoComplete="new-off"
                />
              )}
            />

            <Controller
              name="lastName"
              control={control}
              rules={{
                required: FORM_MESSAGES.required
              }}
              render={({ field }) => (
                <Input
                  label="Last name"
                  {...field}
                  error={errors[field.name]?.message}
                  onBlur={() => {
                    field.onBlur()
                  }}
                  disabled={isUpdatingAccount}
                  data-e2e="last-name-account-details"
                  autoComplete="new-off"
                />
              )}
            />

            <Controller
              name="birthYear"
              control={control}
              rules={{
                required: FORM_MESSAGES.required,
                pattern: {
                  value: FORM_VALUES.birthYear.pattern,
                  message: FORM_MESSAGES.birthYear.valid(ACCOUNT_MIN_AGE)
                },
                minLength: {
                  value: FORM_VALUES.birthYear.length,
                  message: FORM_MESSAGES.birthYear.valid(ACCOUNT_MIN_AGE)
                },
                maxLength: {
                  value: FORM_VALUES.birthYear.length,
                  message: FORM_MESSAGES.birthYear.valid(ACCOUNT_MIN_AGE)
                },
                validate: (value) => {
                  const year = parseInt(value, 10)
                  if (isNaN(year)) return FORM_MESSAGES.birthYear.valid(ACCOUNT_MIN_AGE)
                  if (year < minBirthYear || year > maxBirthYear) {
                    return FORM_MESSAGES.birthYear.valid(ACCOUNT_MIN_AGE)
                  }
                  return true
                }
              }}
              render={({ field }) => (
                <Input
                  label="Year of birth (YYYY)"
                  {...field}
                  error={errors[field.name]?.message}
                  onBlur={() => {
                    field.onBlur()
                    trigger('birthYear')
                  }}
                  tooltip="We’ll use this information to create a more personalised NEON experience"
                  disabled={isUpdatingAccount}
                  data-e2e="year-of-birth-account-details"
                  autoComplete="new-off"
                />
              )}
            />

            <Controller
              name="gender"
              control={control}
              rules={{
                required: FORM_MESSAGES.required
              }}
              render={({ field }) => (
                <Select
                  label="Gender"
                  {...field}
                  error={errors[field.name]?.message}
                  onBlur={() => {
                    field.onBlur()
                  }}
                  options={genderOptions}
                  disabled={isUpdatingAccount}
                  data-e2e="gender-account-details"
                />
              )}
            />

            <div className={styles.greenLink}>
              NEON will use your data to personalise and improve your NEON experience and to send you information about NEON. You can change your communication preferences at any time. We may use your data as described in our&nbsp;
              <a href={STATIC_SCREENS.PRIVACY} target="_blank" rel="noopener noreferrer">
                Privacy Policy.
              </a>
            </div>

            <Button
              className={styles.submit}
              text="Set up payment"
              type="submit"
              disabled={isUpdatingAccount}
              isRental={isRental}
              e2eIdentifier="setup-payment-button-account-details-submit"
            />
            {errors.root?.formError && (
              <p className={styles.errorMessage}>
                {errors.root.formError.message}
              </p>
            )}
          </form>

          <FullScreenLoadingComponent visible={isUpdatingAccount} data-e2e="account-details-loader-visible" />
        </div>
      </div>
    </div>
  )
}

CompleteAccountDetails.propTypes = {
  renderStep: PropTypes.func,
  isRental: PropTypes.bool,
  onCompleteAccountDetails: PropTypes.func
}

CompleteAccountDetails.defaultProps = {
  renderStep: null,
  isRental: false,
  onCompleteAccountDetails: null
}

export default CompleteAccountDetails
